"use client";

import * as Popover from "@radix-ui/react-popover";
import type { ProductDetails, Variant } from "@raumagarn/formatters";
import { clsx } from "@trydig/ui";
import Image from "next/image";
import { Suspense, useEffect, useMemo, useRef } from "react";
import { Callout } from "~/components/ui/Callout";
import { ColorPicker } from "~/components/ui/ColorPicker";
import { Counter } from "~/components/ui/Counter";
import { SizePicker } from "~/components/ui/SizePicker";
import { useSizes } from "~/components/ui/VariantPicker/hooks/useSizes";
import {
	type MappedColor,
	type ProductWithQuantity,
	useVariantPickerState,
} from "~/components/ui/VariantPicker/hooks/useVariantPickerState";
import { useYarnProduct } from "~/components/ui/VariantPicker/hooks/useYarnProduct";
import { Border } from "~/components/ui/VariantPicker/icons/Border";
import { Plus } from "~/components/ui/VariantPicker/icons/Plus";
import styles from "~/components/ui/VariantPicker/VariantPicker.module.css";
import { YarnDropdownContent } from "~/components/ui/YarnSelector/YarnDropdownContent";
import dropdownStyles from "~/components/ui/YarnSelector/yarn-dropdown.module.css";
import { MicroCarts, useMicrocartStore } from "~/features/cart/MicroCart/MicroCartContext";
import { useMicroCart } from "~/features/cart/MicroCart/useMicroCart";
import { VariantContext } from "~/features/products/VariantContext";
import type { RecipePicker } from "~/generated/sb/addtional-types";
import { goodSans } from "~/lib/fonts";
import { useCurrency } from "~/lib/hooks/useCentra";
import { getPrice, usePricelistId } from "~/lib/hooks/usePrice";
import { useDatasource } from "~/lib/storyblok/useDatasource";
import { formatPrice } from "~/lib/utils/price";
import type { ProductData } from "~/srpc/routes/product.router";

// Re-export types for backward compatibility
export type { MappedColor, ProductWithQuantity };

export interface ISize {
	name: string;
	size: string;
	yarn: number[];
}

export type TSize = ISize & {
	selected: boolean;
	select: () => void;
};

// "few" stock left still means available - only "no" stock is actually sold out.
const isSizeSoldOut = (size?: { stock?: unknown; fewLeft?: boolean }) =>
	!size?.stock && !size?.fewLeft;

// Root yarn products expose their Centra variant id as `centraVariant`, but the
// colorways nested under `product.variants[]` only carry it as `id` - matching on
// `centraVariant` alone misses every non-default colorway and wrongly treats an
// available yarn as discontinued (falling back to a `missing-slot-` placeholder).
const findFlattenedYarn = <T extends { centraVariant?: unknown; id?: unknown }>(
	flattened: T[],
	centraVariant: unknown,
) => flattened.find((f) => String(f.centraVariant ?? f.id) === String(centraVariant));

// Fills a slot whose Centra variant no longer exists (discontinued yarn) with a
// generic placeholder, so the slot stays visible and selectable instead of
// disappearing along with its recommended quantity.
const buildFallbackYarn = <T extends { size?: Record<string, unknown> }>(
	slotId: string,
	flattened: T[],
) => {
	const fallbackYarn = flattened[0];
	if (!fallbackYarn) return undefined;

	return {
		...fallbackYarn,
		displayItemId: slotId,
		size: {
			...fallbackYarn.size,
			item: slotId,
		},
	};
};

export interface VariantPickerProps {
	pageData: ProductData["recipeStory"];
	initialProduct?: ProductData["byUri"];
}

const UnsuspendedVariantPicker = ({ pageData, initialProduct }: VariantPickerProps) => {
	if (!initialProduct?.yarns || initialProduct?.yarns?.length === 0 || !pageData) return null;

	// Memoized on the raw Storyblok picker data so the formatted arrays keep a
	// stable identity across re-renders - otherwise every yarn/size object is
	// rebuilt from scratch each render, breaking anything that relies on
	// object identity (e.g. size selection in useSizes).
	const { colorPreset, sizeChart } = useMemo(() => {
		const formatStoryblokReceipe = (picker: RecipePicker) => {
			const colorPreset = picker?.yarns.map((c) => {
				return {
					color: c.name,
					image: c.image,
					yarn: c.value.map((v) => v.variant),
				};
			});

			const sizeChart = picker?.sizes.map((c) => {
				return {
					size: c.name,
					name: c.name,
					yarn: c.value,
				};
			});

			return { colorPreset, sizeChart };
		};

		return pageData?.content.picker
			? formatStoryblokReceipe(pageData.content.picker)
			: { colorPreset: undefined, sizeChart: undefined };
	}, [pageData?.content.picker]);

	const colorPresets = colorPreset as
		| Array<{
				color: string;
				image: string;
				yarn: string[];
		  }>
		| undefined;

	const [sizes, selectedSize, resetSize] = useSizes(sizeChart);
	const { state, actions, derived } = useVariantPickerState();

	const yarnProducts = useYarnProduct(
		Array.from(new Set(initialProduct?.yarns?.map((id) => id.id))),
	);

	// Track previous yarn products length to detect when products finish loading
	const prevYarnProductsLength = useRef(yarnProducts.length);

	// Reset selections when products load for the first time
	useEffect(() => {
		const wasEmpty = prevYarnProductsLength.current === 0;
		const nowHasData = yarnProducts.length > 0;

		if (wasEmpty && nowHasData) {
			// Products just finished loading, reset selections to ensure consistency
			actions.resetAll();
			resetSize();
		}

		prevYarnProductsLength.current = yarnProducts.length;
	}, [yarnProducts.length, actions, resetSize]);

	const colors: MappedColor[] = [
		...(colorPresets || []).map((c) => {
			const flattened = [...yarnProducts.flatMap((p) => p.variants), ...yarnProducts];

			const yarns = c.yarn
				.map((centraVariant, index) => {
					const found = findFlattenedYarn(flattened, centraVariant);

					// Yarn variant no longer exists (discontinued in Centra) - keep the
					// slot as a fallback the customer can fill in, so the recommended
					// quantity for this color doesn't disappear entirely.
					return found || buildFallbackYarn(`missing-slot-${c.color}-${index}`, flattened);
				})
				.filter(Boolean)
				.map((yarn) => {
					const casted = yarn as ProductDetails;
					if (!casted.variants?.length && !casted.displayItemId?.startsWith("missing-slot-")) {
						return {
							...casted,
							variants: flattened.filter((v) => v.centraProduct === yarn!.centraProduct),
						};
					}
					return yarn;
				});

			// Check if all yarns in this color are sold out. Missing/discontinued
			// slots are excluded since the customer can pick a replacement for them.
			const colorSoldOut =
				yarns.length > 0 &&
				yarns.every((yarn) => {
					const productYarn = yarn as ProductDetails;
					if (productYarn.displayItemId?.startsWith("missing-slot-")) {
						return false;
					}
					return isSizeSoldOut(productYarn.size);
				});

			return {
				...c,
				yarn: yarns as unknown as Variant[],
				selected: state.selectedColor?.color === c.color,
				soldOut: colorSoldOut,
			};
		}),
		// Custom "Velg selv" color preset - base it on the first available preset to get correct yarn types
		{
			color: "Velg selv",
			image: "", // Empty image to show custom container,
			yarn:
				selectedSize && (colorPresets?.length || 0) > 0
					? (() => {
							// Use the first color preset as a template to get the correct yarn types
							const templatePreset = colorPresets?.[0];
							const flattened = [...yarnProducts.flatMap((p) => p.variants), ...yarnProducts];

							return templatePreset?.yarn
								.map((centraVariant, index) => {
									// Find the yarn product for this slot from the template
									const templateYarn = findFlattenedYarn(flattened, centraVariant);

									if (templateYarn) {
										// Create a placeholder based on the template yarn but with custom slot ID
										return {
											...templateYarn,
											displayItemId: `custom-slot-${index}`,
											name: templateYarn.name,
											size: {
												...templateYarn.size,
												item: `custom-slot-${index}`,
											},
											// Keep the variants from the template yarn so user can see all options
										};
									}

									// Fallback: use the first available yarn as template to ensure all properties are present
									const fallbackYarn = flattened[0];
									if (fallbackYarn) {
										return {
											...fallbackYarn,
											displayItemId: `custom-slot-${index}`,
											name: fallbackYarn.name,
											size: {
												...fallbackYarn.size,
												item: `custom-slot-${index}`,
											},
										};
									}

									// Last resort: use template from predefined presets
									return templateYarn || flattened[0];
								})
								.filter(Boolean) as unknown as Variant[];
						})()
					: [],
			selected: state.selectedColor?.color === "Velg selv",
			soldOut: false, // Custom selection is never sold out since users can choose from available yarns
		},
	];

	const yarns = derived.buildYarnsArray(state.selectedColor, selectedSize);

	// Check if all yarns across all color presets are sold out
	const allColorVariantYarnsSoldOut = useMemo(() => {
		if (yarnProducts.length === 0) {
			return false; // Can't determine if no data yet
		}

		// Get all predefined color presets (exclude "Velg selv" custom preset)
		const predefinedColors = colorPresets || [];

		if (predefinedColors.length === 0) {
			return false;
		}

		// Collect all unique yarns across all color presets
		const allYarns = predefinedColors.flatMap((color) => {
			const flattened = [...yarnProducts.flatMap((p) => p.variants), ...yarnProducts];

			return color.yarn
				.map((centraVariant) => findFlattenedYarn(flattened, centraVariant))
				.filter(Boolean);
		});

		if (allYarns.length === 0) {
			return false;
		}

		// Check if every yarn is sold out
		return allYarns.every((yarn) => {
			if (!yarn) return true; // Missing yarn counts as sold out
			return isSizeSoldOut(yarn.size);
		});
	}, [colorPresets, yarnProducts]);

	const setAllColorVariantYarnsSoldOutInStore = useMicrocartStore(
		(state) => state.setAllColorVariantYarnsSoldOut,
	);

	// Update the microcart store when the value changes
	useEffect(() => {
		setAllColorVariantYarnsSoldOutInStore(allColorVariantYarnsSoldOut);
	}, [allColorVariantYarnsSoldOut, setAllColorVariantYarnsSoldOutInStore]);

	const currentEditingYarn = useMemo(() => {
		if (!state.currentEditingYarn) return null;

		// If it's a custom or discontinued-yarn slot, provide all available yarn variants
		if (
			state.currentEditingYarn.displayItemId?.startsWith("custom-slot-") ||
			state.currentEditingYarn.displayItemId?.startsWith("missing-slot-")
		) {
			return {
				...state.currentEditingYarn,
				variants: [
					yarnProducts.at(0),
					...(yarnProducts?.flatMap((product) => product.variants) || []).filter(
						(y) => y.centraProduct === state.currentEditingYarn!.centraProduct,
					),
				].filter(Boolean) as Variant[],
			};
		}

		return {
			...state.currentEditingYarn,
			variants: [
				yarnProducts.at(0),
				...yarnProducts
					.flatMap((product) => product.variants)
					.filter((y) => y.centraProduct === state.currentEditingYarn?.centraProduct),
			].filter(Boolean) as Variant[],
		};
	}, [state.currentEditingYarn, yarnProducts]);

	const availableVariants = currentEditingYarn?.variants || [];

	const resetAll = () => {
		actions.resetAll();
		resetSize();
	};

	const pricelistId = usePricelistId();

	// Filter out empty custom/missing slots from cart calculation
	const cartYarns = useMemo(() => {
		return yarns
			.filter((yarn) => {
				const isSelectableSlot =
					yarn?.displayItemId?.startsWith("custom-slot-") ||
					yarn?.displayItemId?.startsWith("missing-slot-");
				if (!isSelectableSlot) return true; // Include normal slots

				// Only include placeholder slots once the user has picked a replacement
				return yarn.size?.item ? !!state.customYarns[yarn.size?.item] : false;
			})
			.map((yarn) => {
				// For a custom/replaced slot, `size.item` is intentionally kept as the
				// slot's own stable id (see SAVE_YARN_SELECTION) so quantity/editing
				// state keeps working across renders - the real Centra item id to add
				// to cart lives in `centraItemId` instead.
				const centraItemId = (yarn as { centraItemId?: string }).centraItemId ?? yarn.size?.item;

				return {
					...yarn,
					// useMicroCart reads a flat `stock` field, not the nested `size.stock`.
					stock: isSizeSoldOut(yarn.size) ? "no" : "yes",
					size: { ...yarn.size, item: centraItemId },
				};
			});
	}, [yarns, state.customYarns]);

	useMicroCart(
		MicroCarts.Yarn,
		cartYarns,
		{
			quantity: "quantity",
			id: "size.item",
			prices: "pricelist",
		},
		resetAll,
	);

	const getLabel = useDatasource("pdp-text");
	const { data: currency } = useCurrency();

	return (
		<section className={clsx("pdp-card variant-picker", goodSans.className)}>
			<div className="pdp-sub-container">
				<p className="pdp-sub-container-title">{getLabel("Garn")}</p>
				<div className={styles["label-container"]}>
					<p className="pdp-sub-container-label">{getLabel("Størrelse")}</p>
					{selectedSize && <p>{selectedSize.name}</p>}
				</div>
				<SizePicker sizes={sizes} />
			</div>
			<div className={styles.container}>
				<div className={styles["label-container"]}>
					<p>{getLabel("Farge")}</p>
				</div>
				<ColorPicker onSelect={actions.setSelectedColor} colors={colors as MappedColor[]} />
			</div>
			{pageData?.content?.infoText && (
				<Callout className={styles.yarnCallout} variant="info">
					{pageData?.content?.infoText}
				</Callout>
			)}
			<div className={styles["yarn-list"]}>
				{yarns.length > 0 ? (
					yarns?.map((yarn, index) => {
						const prices = getPrice({ pricelist: yarn?.pricelist, pricelistId });
						const isMissingSlot = yarn?.displayItemId?.startsWith("missing-slot-");
						const isCustomSlot = yarn?.displayItemId?.startsWith("custom-slot-") || isMissingSlot;
						// Get the original yarn from selectedColor for editing, not the processed one
						const originalYarn = state.selectedColor?.yarn[index];
						// yarn already includes custom overrides from buildYarnsArray via getYarnWithState
						const displayYarn = yarn;
						const soldOut = !!displayYarn && isSizeSoldOut(displayYarn.size);

						const slotId = (originalYarn || yarn)?.size?.item;
						const isEditingThisRow = !!slotId && state.currentEditingYarn?.size?.item === slotId;
						const startEditing = () => {
							actions.startEditingYarn(originalYarn || yarn, state.customYarns);
						};

						return (
							<Popover.Root
								key={`${index} - ${yarn?.name} - yarn-picker `}
								open={isEditingThisRow}
								onOpenChange={(open) => {
									if (!open) actions.cancelYarnEditing();
								}}
							>
								<div
									className={`${styles["yarn-option"]} ${
										selectedSize && state.selectedColor && !soldOut ? "" : styles.inactive
									}`}
								>
									<div className={styles["yarn-details"]}>
										{isCustomSlot && yarn.size?.item && !state.customYarns[yarn.size?.item] ? (
											// Empty slot that can be clicked to select yarn
											<Popover.Trigger asChild>
												<div
													className={`${styles["yarn-placeholder"]} ${styles.custom}`}
													onClick={startEditing}
													style={{ cursor: "pointer" }}
												>
													<Border className={styles["custom-border"]} />
													<Plus className={styles["custom-plus"]} />
												</div>
											</Popover.Trigger>
										) : (
											// Normal yarn display
											<div className={`${styles["yarn-placeholder"]}`}>
												{displayYarn?.media?.[0] && (
													<Image src={displayYarn?.media[0]?.href} alt="" fill />
												)}
											</div>
										)}

										<div className={styles["yarn-name"]}>
											{isCustomSlot && yarn.size?.item && !state.customYarns[yarn.size?.item] ? (
												// Empty custom or discontinued-yarn slot
												<div className={styles["name-container"]}>
													<p>{isMissingSlot ? "Fargen er utgått" : `Velg garn ${yarn.name}`}</p>
													<p className={styles["yarn-detail"]}>
														{isMissingSlot ? "Klikk for å velge en ny farge" : "Klikk for å velge"}
													</p>
												</div>
											) : (
												// Normal yarn or selected custom yarn
												<>
													{displayYarn && (
														<div className={styles["name-container"]}>
															<p>{displayYarn?.name}</p>
															<p className={styles["yarn-detail"]}>{formatPrice(prices.price)}</p>
															{soldOut && <p className={styles["soldout-tag"]}> Utsolgt </p>}
														</div>
													)}
													<p>
														<span>
															{displayYarn?.variantName} ({displayYarn.centraVariant})
														</span>
														<span className={styles["yarn-detail"]}>
															{displayYarn?.weight ?? displayYarn?.weight}
														</span>
													</p>

													{selectedSize && state.selectedColor && (
														<Popover.Trigger asChild>
															<button
																onClick={startEditing}
																className={clsx(styles["yarn-change"], "unstyled")}
															>
																Endre
															</button>
														</Popover.Trigger>
													)}
												</>
											)}
										</div>
									</div>

									<div className={styles["yarn-summary"]}>
										{isCustomSlot && yarn.size?.item && !state.customYarns[yarn.size?.item] ? (
											// Empty custom slot - no counter
											<div className={styles["yarn-lineprice"]}>
												<span>Velg garn først</span>
											</div>
										) : (
											// Normal counter for selected yarns
											<>
												<Counter
													disabled={soldOut}
													max={yarn.recommendedSize}
													value={yarn.quantity}
													decrement={() => {
														if (!yarn.size?.item) return;

														actions.decrementYarnQuantity(
															yarn.size?.item,
															yarn.quantity,
															yarn.recommendedSize,
														);
													}}
													increment={() => {
														if (!yarn.size?.item) return;

														actions.incrementYarnQuantity(
															yarn.size?.item,
															yarn.quantity,
															yarn.recommendedSize,
														);
													}}
													onChange={(e) => {
														if (!yarn.size?.item) return;

														actions.setYarnQuantity(
															yarn.size?.item,
															Math.max(0, Number(e.target.value)),
														);
													}}
												/>
												<div className={styles["yarn-lineprice"]}>
													{yarn.quantity
														? formatPrice((yarn.quantity * (prices.priceAsNumber || 0)).toFixed(2))
														: "0"}{" "}
													{currency}
												</div>
											</>
										)}
									</div>
								</div>

								<Popover.Portal>
									<Popover.Content
										className={dropdownStyles.content}
										sideOffset={8}
										align="start"
										collisionPadding={16}
									>
										<VariantContext
											value={{
												variants: availableVariants,
												currentVariant:
													state.selectedYarnInDialog || state.currentEditingYarn || undefined,
											}}
										>
											<YarnDropdownContent
												onVariantSelect={actions.selectYarnInDialog}
												onSave={actions.saveYarnSelection}
											/>
										</VariantContext>
									</Popover.Content>
								</Popover.Portal>
							</Popover.Root>
						);
					})
				) : (
					<div className={`${styles["yarn-option"]} ${styles.inactive}`}>
						<div className={styles["yarn-details"]}>
							<div className={styles["yarn-placeholder"]} />
							<p>{getLabel("Velg størrelse og farge")}</p>
						</div>
						<Counter value={0} disabled />
					</div>
				)}
			</div>
		</section>
	);
};

export const VariantPicker: typeof UnsuspendedVariantPicker = (props) => {
	return (
		<Suspense>
			<UnsuspendedVariantPicker {...props} />
		</Suspense>
	);
};
